/**
 * centralBillingApi — Bearer-only billing reads/writes against Central.
 *
 * Central is the SOLE authority for subscription AND credit balance.
 *   GET  /api/subscription/me          → SubscriptionInfo
 *   GET  /api/wallet/balance           → { balance }
 *   POST /api/subscriptions/portal     → PortalSessionResponse  { url }
 *
 * All paths come from @levelup/central-billing constants. No local
 * BeHeard billing endpoints are called from the settings tab — Central
 * is the single source of truth. A balance Central did not state (any non-OK
 * answer, an unreadable body, a network failure) is UNKNOWN (null) — never
 * shown as 0 credits.
 *
 * Tolerant response parsing: Central may wrap data in { data: ... } or
 * { subscription: ... } / { wallet: { balance } } etc.
 */

import {
  SUBSCRIPTION_ME_PATH,
  SUBSCRIPTION_PORTAL_PATH,
  WALLET_BALANCE_PATH,
  type SubscriptionInfo,
  type PortalSessionResponse,
} from '@levelup/central-billing';
import { CENTRAL_URL } from '../config/centralAuth';

function authHeaders(token: string, extra: Record<string, string> = {}): Record<string, string> {
  return {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
    ...extra,
  };
}

async function readJson(res: Response): Promise<unknown> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    const message =
      typeof body.message === 'string' ? body.message : `Request failed: ${res.status}`;
    throw new Error(message);
  }
  return res.json();
}

/** Pull a single record out of a `{ data: ... }` / `{ <key>: ... }` envelope. */
function extractRecord<T>(body: unknown, key: string): T | null {
  if (!body || typeof body !== 'object') return null;
  const obj = body as Record<string, unknown>;
  if (obj[key] && typeof obj[key] === 'object') return obj[key] as T;
  if (obj.data && typeof obj.data === 'object') {
    const data = obj.data as Record<string, unknown>;
    if (data[key] && typeof data[key] === 'object') return data[key] as T;
    return data as unknown as T;
  }
  return obj as unknown as T;
}

/**
 * GET /api/subscription/me
 * Returns null if Central has no subscription on file (unauth or missing).
 */
export async function getSubscription(token: string): Promise<SubscriptionInfo | null> {
  const res = await fetch(`${CENTRAL_URL}${SUBSCRIPTION_ME_PATH}`, {
    headers: authHeaders(token),
  });
  if (res.status === 404) return null;
  const body = await readJson(res);
  return extractRecord<SubscriptionInfo>(body, 'subscription');
}

/**
 * GET /api/wallet/balance
 * The balance Central states, or null when it is unknown: a non-OK answer
 * (404 included — "no wallet" is not a stated balance), an unreadable body,
 * or a network failure. Never 0 for "could not read".
 * Tolerant of { balance } | { data: { balance } } | { wallet: { balance } } wrappers.
 */
export async function getWalletBalance(token: string): Promise<number | null> {
  try {
    const res = await fetch(`${CENTRAL_URL}${WALLET_BALANCE_PATH}`, {
      headers: authHeaders(token),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as Record<string, unknown> | null;
    if (!body) return null;
    const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
    if (num(body.balance) !== null) return num(body.balance);
    const data = body.data as Record<string, unknown> | undefined;
    if (data && num(data.balance) !== null) return num(data.balance);
    const wallet = (body.wallet ?? data?.wallet) as Record<string, unknown> | undefined;
    if (wallet && num(wallet.balance) !== null) return num(wallet.balance);
    return null;
  } catch {
    return null;
  }
}

/**
 * POST /api/subscriptions/portal
 * Returns the Stripe Customer Portal URL.
 */
export async function createCentralPortalSession(
  token: string,
  returnUrl: string,
): Promise<string | null> {
  const res = await fetch(`${CENTRAL_URL}${SUBSCRIPTION_PORTAL_PATH}`, {
    method: 'POST',
    headers: authHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify({ returnUrl }),
  });
  const body = await readJson(res);
  // PortalSessionResponse is `{ url }`. Be tolerant of wrappers.
  const obj = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  if (typeof obj.url === 'string') return obj.url;
  const data = obj.data as Record<string, unknown> | undefined;
  if (data && typeof data.url === 'string') return data.url;
  // Some deployments call the field portalUrl
  if (typeof obj.portalUrl === 'string') return obj.portalUrl;
  if (data && typeof data.portalUrl === 'string') return data.portalUrl;
  return null;
}

/**
 * A display name for Central's plan code, or null when Central supplied none
 * (unknown — never assumed to be "Free").
 */
export function planNameFor(planCode: string | undefined): string | null {
  if (!planCode) return null;
  // Title-case the code as a safe default
  return planCode.charAt(0).toUpperCase() + planCode.slice(1);
}

export type { SubscriptionInfo, PortalSessionResponse };
